import Highcharts from "Static/js/highstock";
import sortBy from "lodash/sortBy";
import PropTypes from "prop-types";
import React from "react";
import { utcToLocal } from "util/time";
import { Box } from "view/layout/box";

class StrategyChart extends React.PureComponent {
  static get propTypes() {
    return {
      data: PropTypes.array.isRequired,
      strategies: PropTypes.object.isRequired,
    };
  }

  constructor(props) {
    super(props);

    this.chartElem = null;
    this.highchart = null;

    this.destroy = this.destroy.bind(this);
  }

  componentDidUpdate() {
    this._renderChart(this.props);
  }

  componentDidMount() {
    this._renderChart(this.props);
  }

  componentWillUnmount() {
    this.destroy();
  }

  destroy() {
    if (this.highchart) {
      this.highchart.destroy();
    }
  }

  /*
  Input: [
    [timestamp, strategyId]
  ]
  Output: {
    name: strategyName
      data: [
        name: strategyName
        x: timestamp
        y: onlineStatus
      ]
    }
  */
  _getData(props) {
    const { data, strategies } = props;
    const seriesArr = [];

    if (!data.length) {
      return seriesArr;
    }

    // Keeps track of all online timestamps for each strategy.
    const series = {};
    // Keeps track of all timestamps.
    const allTs = {};
    data.forEach((p) => {
      let [ts, sid] = p;

      if (!sid || sid === "-1") {
        sid = -1;
      }

      allTs[ts] = true;
      if (!(sid in series)) {
        series[sid] = {};
      }

      series[sid][ts] = true;
    });

    for (const [sid, tsMap] of Object.entries(series)) {
      const strategy = strategies[sid];
      const name = strategy ? strategy.name : "N/A";

      // Fill in timestamp gaps for this strategy.
      for (const ts of Object.keys(allTs)) {
        if (!(ts in tsMap)) {
          tsMap[ts] = false;
        }
      }

      const data = [];
      for (const [ts, wasOnline] of Object.entries(tsMap)) {
        data.push({
          name,
          x: Number(ts),
          y: wasOnline ? 1 : null,
        });
      }

      seriesArr.push({
        name,
        data: sortBy(data, "x"),
      });
    }

    return seriesArr;
  }

  _renderChart(props) {
    const noData = props.data.length === 0;
    if (noData) {
      return;
    }

    const { chartElem } = this;
    const opts = {
      chart: {
        renderTo: chartElem,
        type: "area",
      },
      xAxis: {
        type: "datetime",
        labels: {
          enabled: true,
        },
        title: {
          enabled: false,
        },
        gridLineWidth: 0,
      },
      title: {
        text: "",
        style: {
          display: "none",
        },
      },
      tooltip: {
        formatter: function () {
          const eventTime = utcToLocal(this.x);
          return `${this.point.name}<br>${eventTime}`;
        },
        shared: false,
      },
      plotOptions: {
        series: {
          connectNulls: false,
          // Allow per-minute charts
          turboThreshold: 48 * 60,
        },
      },
      yAxis: {
        title: null,
        labels: {
          enabled: false,
        },
        type: "category",
        gridLineWidth: 0,
        minorGridLineWidth: 0,
        startOnTick: false,
        showFirstLabel: true,
        showLastLabel: true,
      },
      series: this._getData(props),
      credits: {
        enabled: false,
      },
    };

    this.destroy();

    this.highchart = new Highcharts.Chart(opts);
  }

  render() {
    const noData = this.props.data.length === 0;

    return (
      <Box title="Strategy Status" type="default">
        <div
          className="chart"
          ref={(elem) => {
            this.chartElem = elem;
          }}
        >
          {noData && <div>No data for chart.</div>}
        </div>
      </Box>
    );
  }
}

export { StrategyChart };
